import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import Link from "next/link";
import { Message } from "semantic-ui-react";
import RoundedButton from "components/RoundedButton";
import { getCta } from 'utils/common';

const OurFavouriteLocalBusinessesArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/our-favourite-local-businesses`;

  return (
    <MainLayout>
      <NextSeo
        title="Our Favourite Local Businesses | DAYMADE"
        description="Take a look at a handful of some of the local businesses we've been supporting"
        openGraph={{
          title: 'Our Favourite Local Businesses | DAYMADE',
          description:
            "Take a look at a handful of some of the local businesses we've been supporting",
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/our-favourite-local-businesses',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: "One of Urban B's art pieces which displays several plants in a hexagonal structure trailing across a wall and above a door",
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'support,local,business,partnership,sustainable,giving back,covid,impact,help,environmental,near me,lifestyle,eco'
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="One of Urban B's art pieces which displays several plants in a hexagonal structure trailing across a wall and above a door"
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            DAYMADE Supporting <strong>Local Businesses</strong>
          </h1>

          <p className="subheading">Our Fave Local Businesses</p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            <span className="bigFirstLetter">H</span>ere at DAYMADE, we LOVE to
            support local businesses up and down the country. We realise the
            importance of aiding each other through difficult times, and we’re
            doing just that through one of our most popular prizes! You select
            your fave small business, we’ll buy you a gift card, and you spend
            it on their fab unique products. What’s not to love?
          </p>

          <p>
            Take a look at a handful of some of the local businesses we’ve been
            supporting…
          </p>

          <h4>Authentic House</h4>

          <p>
            Authentic House, one of our most popular small businesses, provides
            fantastic eco-friendly gift boxes delivered to your door. Filled to
            the brim with a whole range of sustainable and ethical products,
            they help you on your journey of how to lead a more environmentally
            sustainable life. Your box will contain 6-7 fab eco-friendly
            lifestyle products to get you started. All products are
            plastic-free, ethically sourced and cruelty-free, and could range
            from candles to toothbrushes, vegan snacks to cleaning essentials!
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/authentic-house.jpg`}
              alt="eco box filled with sustainable and ethical products, such as candles, soaps, and socks"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/authentic-house-2.jpg`}
              alt="An assortment of eco-friendly products including a reusable tea strainer"
              className="doubleBlogImage"
            />
          </div>

          <p>
            In addition to their environmentally-friendly gift boxes, Authentic
            House also offer a wide range of products on their online site. If
            this is more up your street, we offer £5 and £10 gift vouchers to
            use over at{' '}
            <a
              href="https://authentichouse.co.uk"
              target="_blank"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              Authentic House's website
            </a>{' '}
            – just another way we love supporting a fab small business!
          </p>

          <p>
            Check out what Authentic House has to offer{' '}
            <a
              target="_blank"
              href="https://authentichouse.co.uk"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              here
            </a>
            !
          </p>

          <h4>Urban B</h4>

          <p>
            Our next fab independent business touches a little closer to home.
            One of our very own DAYMADE players makes beautiful handmade ceramic
            goodies in her Shropshire workshop – Urban B! They pride themselves
            on adhering to an exceedingly high standard of handmade quality, and
            their items are sure to liven up ANY room. A whole host of marbled
            goodies range from tealight holders to soap dishes to gorgeous
            ceramic pots with succulents! But don’t worry, these succulents are
            super easy to care for and will get by without needing to be the
            centre of attention.
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/urban-b.jpg`}
              alt="collection of three marbled pots with a succulent in each"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/urban-b-2.jpg`}
              alt="Urban-B team showing of their products in a shop"
              className="doubleBlogImage"
            />
          </div>

          <p>
            Urban B was started by two sisters in 2018, and as they say, the
            rest is history. They’re just as passionate as ever about sourcing
            locally and doing things right. We gotta say, they’re a firm
            favourite!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/hero.jpg`}
            alt="One of Urban B's art pieces which displays several plants in a hexagonal structure trailing across a wall and above a door"
            className="singleBlogImage"
          />

          <p>
            Check out what Urban B has to offer{' '}
            <a
              target="_blank"
              href="https://urban-b.co.uk"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              here
            </a>
            !
          </p>

          <h4>Northern Tea House</h4>

          <p>
            Here’s one for our Huddersfield fans! The Northern Tea House set up
            shop in 2015, after a life-long love of both the classic tea and the
            much-loved afternoon tea. What sets this quaint tea house apart,
            however, is their passion to provide a unique environment for locals
            and visitors alike. Think a chic mix between industrial and modern,
            with a dash of vintage thrown in for good measure.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/northern-tea-house.jpg`}
            alt="Top-down shot of an afternoon tea set up with pancakes and strawberries"
            className="singleBlogImage"
          />

          <p>
            You’ve learned by now that we love supporting independent
            businesses, but it’s true that the Northern Tea House does too! With
            a stunning 8x2m picture wall, they host exhibitions to display
            artists’ work and share it with the world. Gotta keep that circle of
            love and support going! Like many of us, they’ve had to adapt to the
            pandemic, and have come up with an INGENIOUS idea. Take away
            afternoon tea boxes….great thinking or what? So all you
            Huddersfielders, it’s time to enjoy a sophisticated afternoon treat
            AND support a local business at the same time!
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/northern-tea-house-2.jpg`}
              alt="collection of afternoon tea items, including scones, sandwiches, clotted cream, and chocolates"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/northern-tea-house-3.jpg`}
              alt="couple of people sat on an antique sofa enjoying their afternoon at Northern Tea House"
              className="doubleBlogImage"
              style={{
                objectPosition: '100% 25%',
              }}
            />
          </div>

          <p>
            Check out what Northern Tea House has to offer{' '}
            <a
              target="_blank"
              href="https://northernteahouse.co.uk"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              here
            </a>
            !
          </p>

          <h4>Bonearrow</h4>

          <p>
            For those of us who like to go against the grain and opt for unique,
            handmade jewellery, this one’s for you. Clare at Bonearrow designs
            and crafts every piece of stunning jewellery. Her pieces range from
            intricately detailed filigree rings to bold statement necklaces.
            It’s not just Sterling Silver and ethically sourced gemstones that
            you’ll find here though, oh no, you’ll be falling in love with a
            piece of jewellery as unique as you!
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/bonearrow-1.jpg`}
              alt="metal bracelets that look like they are from the medieval period"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/bonearrow-2.jpg`}
              alt="Dark shelves in shop with dozens of pieces of handmade jewelry"
              className="doubleBlogImage"
            />
          </div>

          <p>
            Clare’s diploma in design and degree in Silversmithing taught her
            the technical skills she needed to get creating her own pieces
            (seriously, we wouldn't even know WHERE to begin..!). Having said
            that, it was actually her desire to create jewellery that was
            unconventional and uncommercial that really allowed her to flourish.
            She loves each and every item she designs and makes, and if we’ve
            learned anything from our independent businesses, it’s that their
            passion for their products is unmatchable.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/bonearrow-3.jpg`}
            alt="Various geometric-styled rings with triangle patterns"
            className="singleBlogImage"
          />

          <p>
            We also think it’s worth mentioning that not only are all Bonearrow
            products vegan, but their packaging is also either recycled or
            recyclable AND plastic free. Win for you, win for the planet!
          </p>

          <p>
            Check out what Bonearrow has to offer{' '}
            <a
              target="_blank"
              href="https://bonearrow.com"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              here
            </a>
            !
          </p>

          <h4>Gwen’s Brownie Boxes</h4>

          <p>
            Vegan brownies. Yep, we said it. Vegan FUDGY brownies. And what’s
            even better? 12 vegan fudgy brownies in 4 different flavours,
            delivered to your door! Now you’re talking…about Gwen’s Brownie
            Boxes!
          </p>

          <p>
            We’ve tried these bad boys and we gotta admit, there’s something
            special about them. They’re all made by Gwen herself, fresh for each
            delivery. After being carefully packaged and shipped out to you,
            you’ll find yourself eagerly waiting by the door for the moment they
            arrive (sorry not sorry) ❤️ Trust us, it’s worth it.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/gwens-brownie-box.jpg`}
            alt="Tray of delicious looking brownies"
            className="singleBlogImage"
          />

          <p>
            There’s a new flavour each month (think strawberry shortbread
            blondies, chocolate orange or s’mores!), along with the much-loved
            classics. We’ve opted to offer our DAYMADE prize winners a box of
            four classic flavours: double choc, Biscoff crunch, salted caramel
            pretzel and cookie dough. Which one would you go for?
          </p>

          <p>
            Check out what Gwen’s Brownie Boxes has to offer{' '}
            <a
              target="_blank"
              href="https://gwensbrownieboxes.bigcartel.com"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              here
            </a>
            !
          </p>

          <h4>Natural Edge</h4>

          <p>
            Our last small business shout-out goes to Natural Edge – a family
            run home interiors and gift shop which has been going for over 30
            years! Proud to be nestled among a gorgeous array of independent
            shops in Leigh-on-Sea, Natural Edge prides themselves on providing
            quality products with a friendly, personal customer service
            experience. Products are carefully selected, with each chosen item
            exuding that feel-good factor. One reason we’re a big fan is their
            selection of eco-friendly, reusable lifestyle goodies. Our winners
            have chosen gift vouchers to use at this fab independent business,
            which can also be used on their online shop too for those not in
            Leigh-on-Sea! Woohoo!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/natural-edge.jpg`}
            alt="cute sketch showing the Natural Edge shopfront"
            className="singleBlogImage"
          />

          <p>
            Check out what Natural Edge has to offer{' '}
            <a
              target="_blank"
              href="https://www.facebook.com/Natural-Edge-514024078613541/"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              here
            </a>
            !
          </p>

          <Message>
            Every week we make many winners happy with cool prizes from local
            businesses. You only need to match 2 Main Numbers + Bonus Number in
            one of the DAYMADE {''}
            <Link href="/">
              <a style={{ margin: 0, fontWeight: 'bold' }}>Weekly Draws</a>
            </Link>
            {''} to win some of these prizes, so the chances are in your favour
            — enter now and you may be amongst our next winners!
          </Message>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px' }}
          >
            ENTER NOW
          </RoundedButton>

          <p style={{ marginTop: '2em' }}>
            Do you own or work at a small business in the retail/ecommerce
            space? Please reach out to{' '}
            <a
              href="mailto:lisa@daymade.co.uk"
              style={{ margin: 0, fontWeight: 'bold' }}
            >
              lisa@daymade.co.uk
            </a>{' '}
            to be featured on our prize platform!
          </p>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default OurFavouriteLocalBusinessesArticle;
